import { Add, Delete, Edit, KeyboardArrowDown } from '@mui/icons-material';
import { useTheme } from '@emotion/react';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    ClickAwayListener,
    Dialog,
    DialogContent,
    Grid,
    IconButton,
    MenuItem,
    MenuList,
    Paper,
    Popper,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';
import CommonDialog from '../../../common/CommonDialog';
import { addInvoiceFeesApi, deleteInvoiceFeesApi, getInvoiceFeesListApi, updateInvoiceFeesApi } from '../../../redux/Invoice/action';
import SuccessMessageModal from '../../Common/SuccessMessage';
import FeesModal from './FeesModal';

/**
 * Styling constants
 */
const FEES_TABLE_STYLES = {
    width: '100%',
    p: '12px',
    border: '1px solid rgba(0, 0, 0, 0.10)',
    borderRadius: '8px',
    boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.06)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: '8px',
    backgroundColor: '#FFF',
};

const TABLE_GRID_STYLES = {
    display: 'grid',
    gridTemplateColumns: `
    1.35fr 12px
    1.35fr 20px
    1fr 20px
    1fr 20px
    1fr 20px
    0.8fr
  `,
    alignItems: 'center',
    width: '100%',
    '@media (max-width: 900px)': {
        gridTemplateColumns: '1fr',
        rowGap: '10px',
    },
};

const TABLE_HEADER_STYLES = {
    ...TABLE_GRID_STYLES,
    width: '100%',
    minHeight: '38px',
    backgroundColor: '#F8F8F8',
    px: 2,
    fontSize: '14px',
    fontWeight: 600,
    color: '#1F1F1F',
    '@media (max-width: 900px)': {
        display: 'none',
    },
};

const TABLE_ROW_STYLES = {
    ...TABLE_GRID_STYLES,
    width: '100%',
    minHeight: '60px',
    px: 2,
    py: 1.5,
};

const TYPE_VALUE_STYLES = {
    width: '100%',
    maxWidth: '180px',
    height: '40px',
    border: '1px solid rgba(0, 0, 0, 0.12)',
    borderRadius: '8px',
    boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.06)',
    display: 'grid',
    gridTemplateColumns: '86px 1fr',
    overflow: 'hidden',
    backgroundColor: '#FFF',
    cursor: 'pointer',
    '@media (max-width: 900px)': {
        maxWidth: '100%',
    },
};

const TYPE_LABEL_STYLES = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    px: 1.25,
    fontSize: '14px',
    fontWeight: '400',
    color: '#1F1F1F',
};

const TYPE_AMOUNT_STYLES = {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    fontWeight: 400,
    color: '#1F1F1F',
};

/**
 * Fees Component
 */
const Fees = ({ type = 'invoice' }) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobileView = useMediaQuery(theme.breakpoints.down('md'));

    // Modal state management
    const [isFeesModalOpen, setIsFeesModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

    // Data management state
    const [editingFeeIndex, setEditingFeeIndex] = useState(-1);
    const [feeToDeleteId, setFeeToDeleteId] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    // Loading state indicators
    const [isDeleteInProgress, setIsDeleteInProgress] = useState(false);
    const [isPageLoading, setIsPageLoading] = useState(false);
    const [isFeeOperationInProgress, setIsFeeOperationInProgress] = useState(false);

    // Dropdown state: tracks which fee row has its type dropdown open
    const [openDropdownIndex, setOpenDropdownIndex] = useState(-1);
    const anchorRefs = useRef([]);

    // Get fees list from Redux store
    const feesList = useSelector((state) => state?.invoice?.feesList?.data) || [];

    const fetchFeesList = useCallback(async () => {
        try {
            setIsPageLoading(true);
            await dispatch(getInvoiceFeesListApi({ pagination: false, type: type }));
        } catch (error) {
            setSuccessMessage('Error fetching fees data');
            setIsSuccessModalOpen(true);
            console.error('Error fetching fees:', error);
        } finally {
            setIsPageLoading(false);
        }
    }, [dispatch]);

    useEffect(() => {
        fetchFeesList();
    }, [fetchFeesList]);

    const handleFeeOperation = useCallback(
        async (operationType, feeData) => {
            try {
                setIsFeeOperationInProgress(true);
                setIsPageLoading(true);
                let response;

                if (operationType === 'add') {
                    response = await addInvoiceFeesApi(feeData);
                    setSuccessMessage('Fee added successfully');
                } else {
                    const feeId = feesList[editingFeeIndex]?._id;
                    const currentFee = feesList[editingFeeIndex] || {};
                    response = await updateInvoiceFeesApi(feeId, {
                        ...feeData,
                        isDefaultInvoice: !!currentFee.isDefaultInvoice,
                        isDefaultEstimate: !!currentFee.isDefaultEstimate,
                        isDefaultWorkOrder: !!currentFee.isDefaultWorkOrder,
                    });
                    setSuccessMessage('Fee updated successfully');
                }

                if (response) {
                    setEditingFeeIndex(-1);
                    await fetchFeesList();
                    setIsFeesModalOpen(false);
                    setIsSuccessModalOpen(true);
                }
            } catch (error) {
                toast.error('Operation failed. Please try again.');
                console.error('Error in fee operation:', error);
            } finally {
                setIsFeeOperationInProgress(false);
                setIsPageLoading(false);
            }
        },
        [editingFeeIndex, feesList, fetchFeesList]
    );

    const handleDeleteFee = useCallback(async () => {
        if (!feeToDeleteId) return;
        try {
            setIsDeleteInProgress(true);
            const response = await deleteInvoiceFeesApi(feeToDeleteId);
            if (response) {
                setIsDeleteModalOpen(false);
                setFeeToDeleteId(null);
                await fetchFeesList();
                setSuccessMessage('Fee deleted successfully');
                setIsSuccessModalOpen(true);
            }
        } catch (error) {
            setSuccessMessage('Failed to delete fee. Please try again.');
            setIsSuccessModalOpen(true);
            console.error('Error deleting fee:', error);
        } finally {
            setIsDeleteInProgress(false);
        }
    }, [feeToDeleteId, fetchFeesList]);

    const confirmDeleteFee = useCallback((fee) => {
        if (fee?._id) {
            setFeeToDeleteId(fee._id);
            setIsDeleteModalOpen(true);
        }
    }, []);

    const openEditFeeModal = useCallback((index) => {
        setEditingFeeIndex(index);
        setIsFeesModalOpen(true);
    }, []);

    const toggleDefaultStatus = useCallback(
        async (fee, defaultKey) => {
            try {
                setIsPageLoading(true);
                const updatedFee = {
                    name: fee.name,
                    calculationType: fee.calculationType,
                    percentage: fee.percentage,
                    amount: fee.amount,
                    type: fee.type,
                    isDefaultInvoice: !!fee.isDefaultInvoice,
                    isDefaultEstimate: !!fee.isDefaultEstimate,
                    isDefaultWorkOrder: !!fee.isDefaultWorkOrder,
                    [defaultKey]: !fee[defaultKey],
                };
                if (updatedFee.calculationType === 'percentage') {
                    delete updatedFee.amount;
                } else {
                    delete updatedFee.percentage;
                }
                const response = await updateInvoiceFeesApi(fee._id, updatedFee);
                if (response) {
                    await fetchFeesList();
                }
            } catch (error) {
                setSuccessMessage('Failed to update default status');
                setIsSuccessModalOpen(true);
                console.error('Error updating default status:', error);
            } finally {
                setIsPageLoading(false);
            }
        },
        [fetchFeesList]
    );

    /**
     * Handles changing the calculation type (Cost/Percent) from the dropdown
     * without opening the full edit modal.
     */
    const handleCalculationTypeChange = useCallback(
        async (fee, newCalculationType) => {
            try {
                setOpenDropdownIndex(-1);
                setIsPageLoading(true);

                const updatedFee = {
                    name: fee.name,
                    type: fee.type,
                    isDefaultInvoice: !!fee.isDefaultInvoice,
                    isDefaultEstimate: !!fee.isDefaultEstimate,
                    isDefaultWorkOrder: !!fee.isDefaultWorkOrder,
                    calculationType: newCalculationType,
                };

                if (newCalculationType === 'percentage') {
                    updatedFee.percentage = fee.percentage ?? fee.amount ?? 0;
                } else {
                    updatedFee.amount = fee.amount ?? fee.percentage ?? 0;
                }

                const response = await updateInvoiceFeesApi(fee._id, updatedFee);
                if (response) {
                    await fetchFeesList();
                }
            } catch (error) {
                toast.error('Failed to update fee type. Please try again.');
                console.error('Error updating calculation type:', error);
            } finally {
                setIsPageLoading(false);
            }
        },
        [fetchFeesList]
    );

    const formatPercentage = useCallback((value) => {
        const numValue = typeof value === 'number' ? value : Number(value);
        return isNaN(numValue) ? '0.00' : numValue.toFixed(2);
    }, []);

    const getFeeDisplay = useCallback(
        (fee) => {
            const isCost = fee?.calculationType === 'fixed_amount' || fee?.calculationType === 'cost' || type !== 'invoice';

            if (isCost) {
                const amount = fee?.amount ?? fee?.percentage;
                return {
                    label: 'Cost',
                    symbol: '$',
                    symbolPosition: 'start',
                    value: formatPercentage(amount),
                };
            }

            return {
                label: 'Percent',
                symbol: '%',
                symbolPosition: 'end',
                value: formatPercentage(fee?.percentage),
            };
        },
        [formatPercentage, type]
    );

    const handleCloseFeesModal = useCallback(() => {
        setIsFeesModalOpen(false);
        if (editingFeeIndex !== -1) setEditingFeeIndex(-1);
    }, [editingFeeIndex]);

    const handleCloseDeleteModal = useCallback(() => {
        if (!isDeleteInProgress) {
            setIsDeleteModalOpen(false);
            setFeeToDeleteId(null);
        }
    }, [isDeleteInProgress]);

    const handleToggleDropdown = useCallback((index) => {
        setOpenDropdownIndex((prev) => (prev === index ? -1 : index));
    }, []);

    const handleCloseDropdown = useCallback((index) => {
        setOpenDropdownIndex((prev) => (prev === index ? -1 : prev));
    }, []);

    return (
        <div>
            <Box>
                <Grid
                    container
                    sx={{ boxShadow: 3, bgcolor: 'white' }}
                    justifyContent="space-between"
                    padding="24px"
                    borderTop="none"
                    borderRadius="10px"
                    marginTop="32px"
                >
                    {/* Header */}
                    <Grid item xs={12} sm={4} md={4} lg={3} xl={3}>
                        <Typography variant="h5" sx={{ fontWeight: 400, fontSize: '24px' }}>
                            Fees
                        </Typography>
                    </Grid>

                    {/* Content */}
                    <Grid item xs={12} sm={8} md={8} xl={8} direction="row" spacing={2}>
                        {Array.isArray(feesList) && feesList.length > 0 ? (
                            <Box sx={FEES_TABLE_STYLES}>
                                {/* Table Header */}
                                <Box sx={TABLE_HEADER_STYLES}>
                                    <Box>Name</Box>
                                    <Box />
                                    <Box>Type</Box>
                                    <Box />
                                    <Box>Invoices</Box>
                                    <Box />
                                    <Box>Estimates</Box>
                                    <Box />
                                    <Box>Work Orders</Box>
                                    <Box />
                                    <Box sx={{ justifySelf: 'end' }}>Actions</Box>
                                </Box>

                                {feesList.map((fee, index) => {
                                    const feeDisplay = getFeeDisplay(fee);
                                    const valueNum = Number(feeDisplay.value);
                                    const valueAdornmentHasAmount = String(feeDisplay.value ?? '').trim() !== '' && !Number.isNaN(valueNum) && valueNum !== 0;
                                    const valueAdornmentColor = valueAdornmentHasAmount ? 'rgba(0, 0, 0, 0.87)' : '#939393';

                                    // Ensure ref array is large enough
                                    if (!anchorRefs.current[index]) {
                                        anchorRefs.current[index] = React.createRef();
                                    }

                                    const isDropdownOpen = openDropdownIndex === index;

                                    return (
                                        <Box sx={TABLE_ROW_STYLES} key={`fee-${fee._id || index}`}>
                                            {/* Name */}
                                            <Typography
                                                variant="body1"
                                                className="label-500"
                                                sx={{
                                                    fontSize: '16px',
                                                    color: '#1F1F1F',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {fee.name}
                                            </Typography>

                                            <Box />

                                            {/* Type Dropdown */}
                                            <Box sx={{ position: 'relative' }}>
                                                <Box ref={anchorRefs.current[index]} sx={TYPE_VALUE_STYLES} onClick={() => handleToggleDropdown(index)}>
                                                    <Box sx={TYPE_LABEL_STYLES}>
                                                        {feeDisplay.label}
                                                        <KeyboardArrowDown
                                                            sx={{
                                                                fontSize: 16,
                                                                color: '#646466',
                                                                transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                                                transition: 'transform 0.2s',
                                                            }}
                                                        />
                                                    </Box>

                                                    <Box sx={TYPE_AMOUNT_STYLES}>
                                                        <Box
                                                            sx={{
                                                                width: '1px',
                                                                height: '16px',
                                                                background: 'rgba(0, 0, 0, 0.10)',
                                                                marginRight: '8px',
                                                                flexShrink: 0,
                                                            }}
                                                        />
                                                        {feeDisplay.symbolPosition === 'start' && (
                                                            <Typography
                                                                component="span"
                                                                sx={{
                                                                    color: valueAdornmentColor,
                                                                    fontSize: '14px',
                                                                    mr: '4px',
                                                                }}
                                                            >
                                                                {feeDisplay.symbol}
                                                            </Typography>
                                                        )}
                                                        <Typography
                                                            component="span"
                                                            sx={{
                                                                color: '#1F1F1F',
                                                                fontSize: '14px',
                                                            }}
                                                        >
                                                            {feeDisplay.value}
                                                        </Typography>
                                                        {feeDisplay.symbolPosition === 'end' && (
                                                            <Typography
                                                                component="span"
                                                                sx={{
                                                                    color: valueAdornmentColor,
                                                                    fontSize: '14px',
                                                                    ml: '4px',
                                                                }}
                                                            >
                                                                {feeDisplay.symbol}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>

                                                {/* Dropdown Popper */}
                                                <Popper
                                                    open={isDropdownOpen}
                                                    anchorEl={anchorRefs.current[index]?.current}
                                                    placement="bottom-start"
                                                    style={{ zIndex: 1300 }}
                                                >
                                                    <ClickAwayListener onClickAway={() => handleCloseDropdown(index)}>
                                                        <Paper
                                                            elevation={3}
                                                            sx={{
                                                                borderRadius: '8px',
                                                                overflow: 'hidden',
                                                                minWidth: '140px',
                                                                mt: '4px',
                                                                boxShadow: '0px 4px 12px rgba(0,0,0,0.15)',
                                                            }}
                                                        >
                                                            <MenuList dense sx={{ py: 0.5 }}>
                                                                <MenuItem
                                                                    sx={{ fontWeight: 400, fontSize: '14px', color: '#212121' }}
                                                                    selected={fee.calculationType === 'percentage'}
                                                                    onClick={() => handleCalculationTypeChange(fee, 'percentage')}
                                                                >
                                                                    Percent
                                                                </MenuItem>

                                                                {/* Cost option */}
                                                                <MenuItem
                                                                    sx={{ fontWeight: 400, fontSize: '14px', color: '#212121' }}
                                                                    selected={fee.calculationType === 'fixed_amount' || fee.calculationType === 'cost'}
                                                                    onClick={() => handleCalculationTypeChange(fee, 'fixed_amount')}
                                                                >
                                                                    Cost
                                                                </MenuItem>
                                                            </MenuList>
                                                        </Paper>
                                                    </ClickAwayListener>
                                                </Popper>
                                            </Box>

                                            <Box />

                                            {/* Invoice Default Checkbox */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '36px' }}>
                                                <Checkbox
                                                    checked={!!fee.isDefaultInvoice}
                                                    onChange={() => toggleDefaultStatus(fee, 'isDefaultInvoice')}
                                                    id={`default-fee-checkbox-${index}`}
                                                    sx={{ p: 0, mr: '10px' }}
                                                    icon={
                                                        <span
                                                            style={{
                                                                width: 20,
                                                                height: 20,
                                                                borderRadius: '4px',
                                                                border: '2px solid #E6E6E6',
                                                                backgroundColor: '#fff',
                                                                display: 'inline-block',
                                                                boxSizing: 'border-box',
                                                                boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.06)',
                                                            }}
                                                        />
                                                    }
                                                />
                                                <Typography variant="body2" sx={{ fontSize: '14px', color: '#1F1F1F' }}>
                                                    Default
                                                </Typography>
                                            </Box>

                                            <Box />

                                            {/* Estimate Default Checkbox */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '36px' }}>
                                                <Checkbox
                                                    checked={!!fee.isDefaultEstimate}
                                                    onChange={() => toggleDefaultStatus(fee, 'isDefaultEstimate')}
                                                    id={`estimate-default-fee-checkbox-${index}`}
                                                    sx={{ p: 0, mr: '10px' }}
                                                    icon={
                                                        <span
                                                            style={{
                                                                width: 20,
                                                                height: 20,
                                                                borderRadius: '4px',
                                                                border: '2px solid #E6E6E6',
                                                                backgroundColor: '#fff',
                                                                display: 'inline-block',
                                                                boxSizing: 'border-box',
                                                                boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.06)',
                                                            }}
                                                        />
                                                    }
                                                />
                                                <Typography variant="body2" sx={{ fontSize: '14px', color: '#1F1F1F' }}>
                                                    Default
                                                </Typography>
                                            </Box>

                                            <Box />

                                            {/* Work Order Default Checkbox */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '36px' }}>
                                                <Checkbox
                                                    checked={!!fee.isDefaultWorkOrder}
                                                    onChange={() => toggleDefaultStatus(fee, 'isDefaultWorkOrder')}
                                                    id={`work-order-default-fee-checkbox-${index}`}
                                                    sx={{ p: 0, mr: '10px' }}
                                                    icon={
                                                        <span
                                                            style={{
                                                                width: 20,
                                                                height: 20,
                                                                borderRadius: '4px',
                                                                border: '2px solid #E6E6E6',
                                                                backgroundColor: '#fff',
                                                                display: 'inline-block',
                                                                boxSizing: 'border-box',
                                                                boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.06)',
                                                            }}
                                                        />
                                                    }
                                                />
                                                <Typography variant="body2" sx={{ fontSize: '14px', color: '#1F1F1F' }}>
                                                    Default
                                                </Typography>
                                            </Box>

                                            <Box />

                                            {/* Actions */}
                                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ justifySelf: 'end' }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => openEditFeeModal(index)}
                                                    aria-label="Edit fee"
                                                    sx={{ p: '4px', width: 28, height: 28 }}
                                                >
                                                    <Edit sx={{ fontSize: 20, color: '#646466' }} />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => confirmDeleteFee(fee)}
                                                    aria-label="Delete fee"
                                                    sx={{ p: '4px', width: 28, height: 28 }}
                                                >
                                                    <Delete sx={{ fontSize: 20, color: '#D32F2F' }} />
                                                </IconButton>
                                            </Stack>
                                        </Box>
                                    );
                                })}
                            </Box>
                        ) : (
                            !isPageLoading && (
                                <Typography variant="body1" align="center" width="100%" mt={2}>
                                    No fees have been added yet.
                                </Typography>
                            )
                        )}

                        <Button
                            variant="outlined"
                            startIcon={<Add />}
                            onClick={() => setIsFeesModalOpen(true)}
                            sx={{
                                width: '100px',
                                height: '38px',
                                padding: '6px 12px',
                                marginTop: '13px',
                                borderRadius: '6px',
                                border: '1px solid #006CDD',
                                background: '#FFF',
                                color: '#006CDD',
                                fontFamily: 'Roboto',
                                fontSize: '15px',
                                fontWeight: 500,
                                lineHeight: '26px',
                                textTransform: 'uppercase',
                                '&:hover': {
                                    border: '1px solid #006CDD',
                                    background: '#FFF',
                                },
                            }}
                        >
                            ADD
                        </Button>
                    </Grid>
                </Grid>
            </Box>

            {/* Fees Modal for adding/editing */}
            <FeesModal
                loader={isFeeOperationInProgress}
                toolbarBg="white"
                color="#000000"
                width="500px"
                showBoxShadow={false}
                type={type}
                open={isFeesModalOpen}
                onClose={handleCloseFeesModal}
                onSave={handleFeeOperation}
                updateIndex={editingFeeIndex}
                setUpdateIndex={setEditingFeeIndex}
                discountProgram={feesList}
            />

            {/* Delete Confirmation Dialog */}
            <CommonDialog
                loader={isDeleteInProgress}
                open={isDeleteModalOpen}
                onClose={handleCloseDeleteModal}
                onSave={handleDeleteFee}
                dialogTitle="Delete Confirmation"
                dialogSubtitle="Are you sure you want to delete this fee?"
            />

            {/* Loading Indicator */}
            <Dialog fullScreen={isMobileView} open={isPageLoading} aria-labelledby="loading-dialog">
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            </Dialog>

            {/* Success Message Modal */}
            <SuccessMessageModal message={successMessage} open={isSuccessModalOpen} setOpenSuccessModal={setIsSuccessModalOpen} />
        </div>
    );
};

export default memo(Fees);
